/* eslint-disable @typescript-eslint/no-explicit-any */
import { OverlayView } from "@react-google-maps/api";
import { FaClock } from "react-icons/fa6";
import { ChevronRight } from "lucide-react";
import { HiMapPin } from "react-icons/hi2";
import WarningIcon from "@/svgs/homepage-icons/cat_lvl_warn.svg";
import { useEffect, useState } from "react";

// Simple in-memory cache to avoid repeated reverse-geo lookups for same coords
const REVERSE_GEO_CACHE = new Map<string, string>();

type LatLng = { lat: number; lng: number };

export type CustomMapMarkerProps = {
  position: LatLng;
  eventTitle: string;
  level?: string; // e.g., "CATEGORY-1"
  place?: string;
  eventLocationName?: string | null; // API-provided location name (from user_country_view/user_worldwide_view)
  eventTime?: string; // backend UTC string
  toDo?: string;
  onOpenSafetyMeasures?: (payload: { place: string | null }) => void;
  minimal?: boolean; // when true, render only the dot marker
  showSafetyButton?: boolean; // when false, hide the safety measures button (used on landing)
  isExpanded?: boolean; // when true, show the overlay bubble
  onDotClick?: () => void; // callback when the green dot is clicked
  alwaysShowOverlay?: boolean; // when true, show overlay by default (e.g. landing page)
}

const getLevelLabel = (level?: string) => {
  const map: Record<string, string> = {
    "CATEGORY-1": "Lv. 1",
    "CATEGORY-2": "Lv. 2",
    "CATEGORY-3": "Lv. 3",
    "CATEGORY-4": "Lv. 4",
    "CATEGORY-5": "Lv. 5",
  };
  return map[level ?? ""] ?? "Lv. 1";
};

const formatLocalTime = (value?: string) => {
  if (!value) return "";
  let date: Date | null = null;
  
  // 1) Try strict "YYYY-MM-DD HH:mm:ss(.fractions) UTC"
  const strict = value.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})(\.\d+)?(?: UTC)?$/
  );
  if (strict) {
    const [, y, mo, d, h, mi, s, frac] = strict;
    let ms = 0;
    if (frac && frac.length > 1) {
      // Keep only first 3 decimals (milliseconds)
      const trimmed = frac.slice(1).padEnd(3, "0").slice(0, 3);
      ms = Number(trimmed);
    }
    // Create UTC date - Date constructor will automatically convert to local timezone when formatted
    date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s), ms));
  } else {
    // 2) Fallback: let Date parse common ISO / offset formats directly
    // Date constructor automatically handles UTC strings and converts to local timezone
    try {
      date = new Date(value);
    } catch {
      return "";
    }
  }
  
  if (!date || isNaN(date.getTime())) return "";
  
  // Format in user's local timezone (Intl.DateTimeFormat with undefined uses user's locale/timezone)
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Explicitly use user's timezone
  }).format(date);
};

export function CustomMapMarker({
  position,
  eventTitle,
  level,
  place,
  eventLocationName,
  eventTime,
  onOpenSafetyMeasures,
  minimal = false,
  showSafetyButton = true,
  isExpanded = false,
  onDotClick,
  alwaysShowOverlay = false,
}: CustomMapMarkerProps) {
  const bubbleWidth = 260;
  const bubbleHeight = 168; // rough height used for offset
  const DOT_SIZE = 20; // base dot size used for vertical alignment
  const [resolvedPlace, setResolvedPlace] = useState<string | null>(place ?? null);
  const showOverlay = alwaysShowOverlay || isExpanded;

  // Only trigger reverse geocoding when overlay is shown AND event_location_name is null/undefined
  useEffect(() => {
    if (minimal || !showOverlay) return; // skip reverse-geo if minimal or overlay not shown
    // If event_location_name is provided and not null, skip reverse geocoding
    if (eventLocationName != null && eventLocationName !== "") return;
    let cancelled = false;
    async function resolvePlace() {
      const lat = position?.lat;
      const lng = position?.lng;
      if (typeof lat !== "number" || typeof lng !== "number") return;
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      // serve from cache if available
      if (REVERSE_GEO_CACHE.has(key)) {
        if (!cancelled) setResolvedPlace(REVERSE_GEO_CACHE.get(key) ?? null);
        return;
      }
      try {
        const res = await fetch("/api/reverse-geo", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ latitude: lat, longitude: lng }),
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) {
          const detected = json?.place ?? null;
          if (detected) {
            REVERSE_GEO_CACHE.set(key, detected);
          }
          setResolvedPlace(detected);
        }
      } catch {
        // ignore
      }
    }
    resolvePlace();
    return () => {
      cancelled = true;
    };
  }, [position?.lat, position?.lng, minimal, showOverlay, eventLocationName]);

  return (
    <>
      {!minimal && showOverlay && (
    <>
      {/* Bubble */}
      <OverlayView
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(w, h) => {
          const width = w ?? bubbleWidth;
          const height = h ?? bubbleHeight;
          // Position bubble so its bottom midpoint sits on top-center of the base dot (anchor is dot center)
          return { x: -Math.round(width / 2), y: -Math.round(height) - Math.round(DOT_SIZE / 2) };
        }}
      >
        <div
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            width: bubbleWidth,
            background: "#FFFFFF",
            borderRadius: 24,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.08)",
            padding: 18,
            position: "relative",
          }}
        >
          {/* Pointer */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              // keep the pointer touching the bubble bottom (not below the anchor)
              bottom: 0,
              width: 14,
              height: 14,
              background: "#FFFFFF",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.08)",
              transformOrigin: "center",
              rotate: "45deg",
              borderRadius: 3,
            }}
          />

          {/* Title row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginBottom: 10 }}>
            <span className="font-syne" style={{ color: "#12141D", fontWeight: 700, textTransform: "capitalize" }}>
              {eventTitle}
            </span>
            <WarningIcon className="w-4 h-4" />
            <span className="font-syne" style={{ color: "#F44336", fontWeight: 700 }}>{getLevelLabel(level)}</span>
          </div>

          {/* Place row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, }}>
            <HiMapPin size={18} color="#6B7280" />
            <span className="font-syne" style={{ color: "#12141D", fontSize: 14 }}>
              <span style={{ color: "#6B7280", marginRight: 6 }}>Place:</span>
              {eventLocationName ?? resolvedPlace ?? place ?? "Unknown"}
            </span>
          </div>

          {/* Time row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }} >
            <FaClock color="#6B7280" size={13} style={{ marginLeft: ".17rem" }} />
            <span className="font-syne" style={{ color: "#12141D", fontSize: 14 }}>
              <span style={{ color: "#6B7280", marginRight: 6 }}>Time</span>
              {formatLocalTime(eventTime)}
            </span>
          </div>

          {/* Event details */}
          {showSafetyButton ? (
            <button
              type="button"
              data-safety-measure-trigger
              onClick={(e) => {
                e.stopPropagation();
                onOpenSafetyMeasures?.({ place: eventLocationName ?? resolvedPlace ?? place ?? null });
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#EEF2F4",
                borderRadius: 12,
                padding: "12px 14px",
                color: "#12141D",
              }}
            >
              <span className="font-syne" style={{ fontSize: 14 }}>Event Details</span>
              <ChevronRight size={18} color="#111827" />
            </button>
          ) : null}
        </div>
      </OverlayView>
        </>
      )}

      {/* Base dot marker - clickable */}
      <OverlayView
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(w, h) => {
          return { x: -Math.round(DOT_SIZE / 2), y: -Math.round(DOT_SIZE / 2) };
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDotClick?.();
          }}
          style={{
            width: DOT_SIZE,
            height: DOT_SIZE,
            borderRadius: "50%",
            background: "tomato",
            boxShadow: "0 0 0 8px rgba(255, 99, 71, 0.25)",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "block",
          }}
          aria-label={`View details for ${eventTitle}`}
        />
      </OverlayView>
    </>
  );
}


